/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import * as DashboardAPIs from '../services/DashboardAPIs';
import { fetchMonitorDashboards } from '../../Monitor/actions/monitorActionCreators';

import { RESOURCE_TYPES } from 'dataConstants';

export const GET_DASHBOARD_TEMPLATE = 'GET_DASHBOARD_TEMPLATE';
export const GET_DASHBOARD_TEMPLATE_SUCCESS = 'GET_DASHBOARD_TEMPLATE_SUCCESS';
export const GET_DASHBOARD_TEMPLATE_FAILURE = 'GET_DASHBOARD_TEMPLATE_FAILURE';
export const CLEAR_DASHBOARD_TEMPLATE = 'CLEAR_DASHBOARD_TEMPLATE';
export const UPDATE_DASHBOARD_TEMPLATE = 'UPDATE_DASHBOARD_TEMPLATE';
export const UPDATE_DASHBOARD_TEMPLATE_SUCCESS = 'UPDATE_DASHBOARD_TEMPLATE_SUCCESS';
export const UPDATE_DASHBOARD_TEMPLATE_FAILURE = 'UPDATE_DASHBOARD_TEMPLATE_FAILURE';

export const DELETE_DASHBOARD_TEMPLATE = 'DELETE_DASHBOARD_TEMPLATE';
export const DELETE_DASHBOARD_TEMPLATE_SUCCESS = 'DELETE_DASHBOARD_TEMPLATE_SUCCESS';
export const DELETE_DASHBOARD_TEMPLATE_FAILURE = 'DELETE_DASHBOARD_TEMPLATE_FAILURE';

const getDashboardTemplate = () => ({ type: GET_DASHBOARD_TEMPLATE });
const getDashboardTemplateSuccess = payload => ({
  type: GET_DASHBOARD_TEMPLATE_SUCCESS,
  payload,
});
const getInstanceDashboardFailure = payload => ({
  type: GET_DASHBOARD_TEMPLATE_FAILURE,
  payload,
});
export const clearDashboardTemplate = () => ({ type: CLEAR_DASHBOARD_TEMPLATE });

export const fetchDashboardTemplate = (entityType, entityId, dashboardId) => dispatch => {
  dispatch(getDashboardTemplate());
  // TODO: temporary workaround to only fetch the first one, removed once we support tabbed instance dashboards
  return DashboardAPIs.getInstanceDashboard(entityType, entityId, dashboardId)
    .then(payload => {
      dispatch(getDashboardTemplateSuccess(payload));
      return payload;
    })
    .catch(payload => {
      dispatch(getInstanceDashboardFailure(payload));
    });
};

const updateDashboardTemplateStart = () => ({
  type: UPDATE_DASHBOARD_TEMPLATE,
});
const updateDashboardTemplateSuccess = payload => ({
  type: UPDATE_DASHBOARD_TEMPLATE_SUCCESS,
  payload,
});
const updateDashboardTemplateFailure = payload => ({
  type: UPDATE_DASHBOARD_TEMPLATE_FAILURE,
  payload,
});

export const updateDashboardTemplate =
  (entityType, entityId, template) =>
  (dispatch, getState__, { dataCache }) => {
    dispatch(updateDashboardTemplateStart());
    return DashboardAPIs.updateInstanceDashboard(
      entityType,
      entityId,
      template,
      dataCache.get('currentUser')?.id
    )
      .then(payload => {
        dispatch(updateDashboardTemplateSuccess(payload));
        return payload;
      })
      .catch(payload => {
        dispatch(updateDashboardTemplateFailure(payload));
        throw payload;
      });
  };

/**
 *
 * this is a special case of update that calls a different API in the backend
 * @param {string} deviceTypeName
 * @param {string} deviceId
 * @param {*} dashboardMetaId
 * @param {number} sequenceNumber
 * @returns
 */
export const updateDashboardSequence =
  (deviceTypeName, deviceId, dashboardMetaId, sequenceNumber, type = RESOURCE_TYPES.DEVICE) =>
  (dispatch, getState__, { dataCache }) => {
    dispatch(updateDashboardTemplateStart());
    return DashboardAPIs.updateDashboardSequenceV1API(
      deviceTypeName,
      deviceId,
      dashboardMetaId,
      sequenceNumber,
      dataCache.get('currentUser')?.id
    )
      .then(() => {
        // Need to refetch all dashboards because the sequence numbers might have changed
        fetchMonitorDashboards(deviceTypeName, deviceId, type);
      })
      .catch(error => {
        dispatch(updateDashboardTemplateFailure(error));
        throw error;
      });
  };

/**
 * this is a special case of update that calls a different API in the backend to update the isEnabled bit
 * @param {*} deviceTypeName
 * @param {*} deviceId
 * @param {*} dashboardMetaId
 * @param {*} isEnabled
 * @returns
 */
export const updateDashboardEnabledStatus =
  (deviceTypeName, deviceId, dashboardMetaId, isEnabled) =>
  (dispatch, getState__, { dataCache }) => {
    dispatch(updateDashboardTemplateStart());
    return DashboardAPIs.updateDashboardEnabledStatusV1API(
      deviceTypeName,
      deviceId,
      dashboardMetaId,
      isEnabled,
      dataCache.get('currentUser')?.id
    )
      .then(responseJson => {
        dispatch(updateDashboardTemplateSuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(updateDashboardTemplateFailure(error));
        throw error;
      });
  };

export const createDashboardTemplate =
  (entityType, entityId, template) =>
  (dispatch, getState__, { dataCache }) => {
    dispatch(updateDashboardTemplateStart());
    return DashboardAPIs.createDashboardV1API(
      entityType,
      entityId,
      template,
      dataCache.get('currentUser')?.id
    )
      .then(payload => {
        dispatch(updateDashboardTemplateSuccess(template));
        return payload;
      })
      .catch(payload => {
        dispatch(updateDashboardTemplateFailure(payload));
        throw payload;
      });
  };

const deleteDashboardStart = () => ({
  type: DELETE_DASHBOARD_TEMPLATE,
});
const deleteDashboardSuccess = payload => ({
  type: DELETE_DASHBOARD_TEMPLATE_SUCCESS,
  payload,
});
const deleteDashboardFailure = payload => ({
  type: DELETE_DASHBOARD_TEMPLATE_FAILURE,
  payload,
});

/**
 *  deletes the dashboard
 * @param(template): the entire dashboard object to delete
 */
export const deleteDashboard =
  (entityType, entityId, template) =>
  (dispatch, getState__, { dataCache }) => {
    dispatch(deleteDashboardStart());
    return DashboardAPIs.deleteDashboardV1API(
      entityType,
      entityId,
      template?.dashboardMetaId,
      dataCache.get('currentUser')?.id
    )
      .then(payload => {
        dispatch(deleteDashboardSuccess(template));
        return payload;
      })
      .catch(payload => {
        dispatch(deleteDashboardFailure(payload));
        throw payload;
      });
  };
